import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAdmin } from '@/contexts/AdminContext';
import axios from 'axios';
import { ArrowLeft, Plus, Trash2, Eye, EyeOff, GripVertical, Save } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminCountries = () => {
  const { isAdmin, loading: authLoading } = useAdmin();
  const navigate = useNavigate();
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCountry, setNewCountry] = useState({
    code: '',
    name: '',
    flag_url: '',
    keywords: '',
    active: true
  });

  const adminPassword = localStorage.getItem('adminPassword') || '';

  const fetchCountries = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/countries`);
      setCountries(response.data.sort((a, b) => a.sort_order - b.sort_order));
    } catch (error) {
      console.error('Error fetching countries:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!isAdmin) {
      navigate('/admin/login');
      return;
    }
    fetchCountries();
  }, [isAdmin, authLoading, navigate, fetchCountries]);

  const handleSeedCountries = async () => {
    if (!window.confirm('Vuoi caricare le nazioni predefinite? (Funziona solo se il database è vuoto)')) return;
    
    try {
      setSaving(true);
      await axios.post(`${API}/countries/seed`, {}, {
        headers: { 'X-Admin-Password': adminPassword }
      });
      fetchCountries();
    } catch (error) {
      alert('Errore: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (country) => {
    try {
      await axios.put(`${API}/countries/${country.id}`, 
        { active: !country.active },
        { headers: { 'X-Admin-Password': adminPassword } }
      );
      fetchCountries();
    } catch (error) {
      alert('Errore: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDelete = async (country) => {
    if (!window.confirm(`Sei sicuro di voler eliminare "${country.name}"?`)) return;
    
    try {
      await axios.delete(`${API}/countries/${country.id}`, {
        headers: { 'X-Admin-Password': adminPassword }
      });
      fetchCountries();
    } catch (error) {
      alert('Errore: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleAddCountry = async (e) => {
    e.preventDefault();
    if (!newCountry.code || !newCountry.name || !newCountry.flag_url) {
      alert('Compila tutti i campi obbligatori');
      return;
    }

    try {
      setSaving(true);
      const maxOrder = countries.length > 0 ? Math.max(...countries.map(c => c.sort_order)) : 0;
      await axios.post(`${API}/countries`, {
        ...newCountry,
        keywords: newCountry.keywords.split(',').map(k => k.trim()).filter(k => k),
        sort_order: maxOrder + 1
      }, {
        headers: { 'X-Admin-Password': adminPassword }
      });
      setNewCountry({ code: '', name: '', flag_url: '', keywords: '', active: true });
      setShowAddForm(false);
      fetchCountries();
    } catch (error) {
      alert('Errore: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateOrder = async (countryId, newOrder) => {
    try {
      await axios.put(`${API}/countries/${countryId}`, 
        { sort_order: newOrder },
        { headers: { 'X-Admin-Password': adminPassword } }
      );
      fetchCountries();
    } catch (error) {
      console.error('Error updating order:', error);
    }
  };

  const moveCountry = (index, direction) => {
    const newCountries = [...countries];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newCountries.length) return;
    
    // Swap sort_order values
    const currentOrder = newCountries[index].sort_order;
    const targetOrder = newCountries[targetIndex].sort_order;
    
    handleUpdateOrder(newCountries[index].id, targetOrder);
    handleUpdateOrder(newCountries[targetIndex].id, currentOrder);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-blue-600 text-white py-6 shadow-lg">
        <div className="max-w-4xl mx-auto px-4">
          <h1 className="text-3xl font-bold">Gestione Nazioni</h1>
          <p className="text-blue-100 mt-1">Aggiungi, modifica o nascondi le nazioni dalla pagina eventi</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Navigation */}
        <div className="mb-6 flex items-center justify-between">
          <Link
            to="/admin/dashboard"
            className="flex items-center text-blue-600 hover:text-blue-800 font-semibold"
          >
            <ArrowLeft className="mr-2" size={20} />
            Torna alla Dashboard
          </Link>

          <div className="flex gap-3">
            {countries.length === 0 && (
              <button
                onClick={handleSeedCountries}
                disabled={saving}
                className="flex items-center bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:bg-gray-400"
              >
                <Save className="mr-2" size={18} />
                Carica Predefinite
              </button>
            )}
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="flex items-center bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              <Plus className="mr-2" size={18} />
              Aggiungi Nazione
            </button>
          </div>
        </div>

        {/* Add Form */}
        {showAddForm && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <h2 className="text-xl font-bold mb-4">Nuova Nazione</h2>
            <form onSubmit={handleAddCountry} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Codice * <span className="text-gray-500 text-xs">(es: morocco, algeria)</span>
                  </label>
                  <input
                    type="text"
                    value={newCountry.code}
                    onChange={(e) => setNewCountry({...newCountry, code: e.target.value.toLowerCase().replace(/\s/g, '_')})}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="morocco"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome Visualizzato *
                  </label>
                  <input
                    type="text"
                    value={newCountry.name}
                    onChange={(e) => setNewCountry({...newCountry, name: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Marocco"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL Bandiera * <span className="text-gray-500 text-xs">(usa flagcdn.com/w80/XX.png)</span>
                </label>
                <input
                  type="url"
                  value={newCountry.flag_url}
                  onChange={(e) => setNewCountry({...newCountry, flag_url: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="https://flagcdn.com/w80/ma.png"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Trova i codici su: <a href="https://flagcdn.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">flagcdn.com</a> (es: it, tn, ma, dz, eu)
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Parole chiave <span className="text-gray-500 text-xs">(separate da virgola, per matching automatico)</span>
                </label>
                <input
                  type="text"
                  value={newCountry.keywords}
                  onChange={(e) => setNewCountry({...newCountry, keywords: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="marocco, morocco, marrakech"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={newCountry.active}
                  onChange={(e) => setNewCountry({...newCountry, active: e.target.checked})}
                  className="w-4 h-4"
                />
                <label className="text-sm text-gray-700">Visibile nella pagina eventi</label>
              </div>
              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={saving}
                  className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 disabled:bg-gray-400"
                >
                  {saving ? 'Salvataggio...' : 'Salva'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="bg-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-400"
                >
                  Annulla
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Countries List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : countries.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl shadow">
            <p className="text-gray-500 mb-4">Nessuna nazione configurata.</p>
            <button
              onClick={handleSeedCountries}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
            >
              Carica Nazioni Predefinite
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="p-4 border-b bg-gray-50">
              <p className="text-sm text-gray-600">
                <strong>{countries.filter(c => c.active).length}</strong> nazioni visibili su <strong>{countries.length}</strong> totali
              </p>
            </div>
            <div className="divide-y">
              {countries.map((country, index) => (
                <div 
                  key={country.id} 
                  className={`p-4 flex items-center gap-4 ${!country.active ? 'bg-gray-50 opacity-60' : ''}`}
                >
                  {/* Drag Handle / Order */}
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={() => moveCountry(index, 'up')}
                      disabled={index === 0}
                      className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                      title="Sposta su"
                    >
                      ▲
                    </button>
                    <span className="text-xs text-gray-400 text-center">{country.sort_order}</span>
                    <button
                      onClick={() => moveCountry(index, 'down')}
                      disabled={index === countries.length - 1}
                      className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                      title="Sposta giù"
                    >
                      ▼
                    </button>
                  </div>

                  {/* Flag */}
                  <img
                    src={country.flag_url}
                    alt={country.name}
                    className="w-12 h-8 object-cover rounded shadow"
                    onError={(e) => { e.target.src = 'https://via.placeholder.com/48x32?text=?'; }}
                  />

                  {/* Info */}
                  <div className="flex-grow">
                    <p className="font-semibold text-gray-900">{country.name}</p>
                    <p className="text-sm text-gray-500">
                      <code className="bg-gray-100 px-1 rounded">{country.code}</code>
                      {country.keywords?.length > 0 && (
                        <span className="ml-2">
                          Keywords: {country.keywords.join(', ')}
                        </span>
                      )}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggleActive(country)}
                      className={`p-2 rounded-lg ${country.active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'}`}
                      title={country.active ? 'Nascondi' : 'Mostra'}
                    >
                      {country.active ? <Eye size={18} /> : <EyeOff size={18} />}
                    </button>
                    <button
                      onClick={() => handleDelete(country)}
                      className="p-2 rounded-lg bg-red-100 text-red-600 hover:bg-red-200"
                      title="Elimina"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Help */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-semibold text-blue-800 mb-2">Come funziona</h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• Le nazioni <strong>attive</strong> appaiono nella pagina Eventi come bandiere cliccabili</li>
            <li>• L'<strong>ordine</strong> determina la posizione delle bandiere (usa le frecce per riordinare)</li>
            <li>• Le <strong>parole chiave</strong> servono per associare automaticamente eventi legacy senza campo "country"</li>
            <li>• Per la bandiera EU: <code>https://flagcdn.com/w80/eu.png</code></li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdminCountries;
